import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Eye, Image as ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import RichTextEditor from "@/admin/components/RichTextEditor";
import MediaPicker from "@/admin/components/MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_PAGE = {
  slug: "",
  title: "",
  subtitle: "",
  hero_image: null,
  body_html: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  published: false,
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

export default function AdminPageEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug || slug === "new";

  const [form, setForm] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/pages/${slug}`);
        setForm({
          slug: data.slug,
          title: data.title || "",
          subtitle: data.subtitle || "",
          hero_image: data.hero_image || null,
          body_html: data.body_html || "",
          seo_title: data.seo_title || "",
          seo_description: data.seo_description || "",
          seo_keywords: data.seo_keywords || "",
          published: !!data.published,
        });
      } catch {
        toast.error("Page not found.");
        navigate("/admin/pages");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    if (isNew && !form.slug.trim()) return toast.error("Slug is required.");
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form, slug: form.slug || slugify(form.title) };
        await axios.post(`${API}/admin/pages`, payload);
        toast.success("Page created.");
        navigate(`/admin/pages/${payload.slug}`);
      } else {
        const patch = { ...form };
        delete patch.slug;
        await axios.patch(`${API}/admin/pages/${slug}`, patch);
        toast.success("Saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-sm text-slate-400" data-testid="admin-page-edit-loading">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-page-edit-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="px-5 lg:px-8 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between sticky top-0 bg-white z-10">
        <div className="flex items-center gap-3">
          <Link to="/admin/pages" data-testid="admin-page-edit-back" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-base font-bold text-slate-900">
              {isNew ? "New Page" : form.title || "Edit Page"}
            </h1>
            <p className="text-[11px] text-slate-400 font-mono mt-0.5">
              {isNew ? "Draft" : `/p/${form.slug}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && form.published && (
            <Link to={`/p/${form.slug}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="admin-page-edit-preview" className="rounded-lg text-xs">
                <Eye className="h-3.5 w-3.5 mr-1" /> Preview
              </Button>
            </Link>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-page-edit-save"
            className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs h-9"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : isNew ? "Create page" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Page title *</Label>
            <Input
              value={form.title}
              onChange={(e) => {
                const title = e.target.value;
                update({ title, slug: isNew && !form.slug ? slugify(title) : form.slug });
              }}
              placeholder="e.g. About Antera"
              data-testid="admin-page-edit-title"
              className="mt-1.5 h-10"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">URL Slug *</Label>
            <Input
              value={form.slug}
              onChange={(e) => update({ slug: slugify(e.target.value) })}
              disabled={!isNew}
              placeholder="e.g. about-antera"
              data-testid="admin-page-edit-slug"
              className="mt-1.5 h-10 font-mono text-xs"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Becomes <span className="font-mono">/p/{form.slug || "<slug>"}</span>. Cannot be changed after creation.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Subtitle (optional)</Label>
            <Textarea
              value={form.subtitle}
              onChange={(e) => update({ subtitle: e.target.value })}
              rows={2}
              placeholder="Short paragraph shown under the title in the hero."
              data-testid="admin-page-edit-subtitle"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Hero image (optional)</Label>
            <div className="mt-1.5 space-y-2" data-testid="admin-page-edit-hero">
              {form.hero_image && (
                <div className="rounded-lg overflow-hidden border border-slate-200 relative">
                  <img src={form.hero_image} alt="" className="w-full max-h-56 object-cover" />
                  <button
                    type="button"
                    onClick={() => update({ hero_image: null })}
                    className="absolute top-2 right-2 bg-white/90 text-slate-600 hover:text-red-500 rounded-full p-1 shadow"
                    aria-label="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPickerOpen(true)}
                  className="h-9 text-xs rounded-lg shrink-0"
                >
                  <ImageIcon className="h-3.5 w-3.5 mr-1" />
                  {form.hero_image ? "Change" : "Pick from library"}
                </Button>
                <Input
                  value={form.hero_image || ""}
                  onChange={(e) => update({ hero_image: e.target.value || null })}
                  placeholder="…or paste URL"
                  className="text-xs font-mono h-9"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Page body</Label>
            <p className="text-[11px] text-slate-400 mt-0.5 mb-2">
              Full rich-text - headings, paragraphs, images, videos, quotes, bullets, links.
            </p>
            <RichTextEditor
              value={form.body_html}
              onChange={(html) => update({ body_html: html })}
              placeholder="Write your page content…"
              testId="admin-page-edit-body"
            />
          </div>

          {/* SEO */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">SEO</h3>
            <div>
              <Label className="text-xs text-slate-600">SEO title</Label>
              <Input
                value={form.seo_title}
                onChange={(e) => update({ seo_title: e.target.value })}
                placeholder="Defaults to page title"
                data-testid="admin-page-edit-seotitle"
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Meta description</Label>
              <Textarea
                value={form.seo_description}
                onChange={(e) => update({ seo_description: e.target.value })}
                rows={2}
                placeholder="Defaults to subtitle"
                data-testid="admin-page-edit-seodesc"
                className="mt-1 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Keywords (comma-separated)</Label>
              <Input
                value={form.seo_keywords}
                onChange={(e) => update({ seo_keywords: e.target.value })}
                placeholder="antera, promotional, erp"
                data-testid="admin-page-edit-seokw"
                className="mt-1 h-9 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-5">
          <div className="border border-slate-200 rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Visibility</h3>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => update({ published: e.target.checked })}
                data-testid="admin-page-edit-published"
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              <span className="text-sm text-slate-700">Published (visible on the site)</span>
            </label>
            <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
              Draft pages return 404 to the public. Only signed-in admins with the direct URL can preview.
            </p>
          </div>

          <div className="border border-sky-100 bg-sky-50/60 rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-sky-700 mb-2">💡 Tip</h3>
            <p className="text-xs text-sky-900/80 leading-relaxed">
              To link this page from the site nav or footer, go to <Link to="/admin/site-settings" className="underline">Site Settings</Link> after publishing.
            </p>
          </div>
        </div>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(url) => {
          update({ hero_image: url });
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
